export interface Point {
  x: number;
  y: number;
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Returns Minimum Translation Vector to push r1 out of r2.
 * Returns null if no intersection.
 */
export function getRectMtv(r1: Rect, r2: Rect): Point | null {
  const left = r2.x - (r1.x + r1.width);
  const right = r2.x + r2.width - r1.x;
  const top = r2.y - (r1.y + r1.height);
  const bottom = r2.y + r2.height - r1.y;

  if (left >= 0 || right <= 0 || top >= 0 || bottom <= 0) {
    return null;
  }

  // Find the smallest overlap
  const overlaps = [left, right, top, bottom];
  const minOverlap = Math.min(...overlaps.map(Math.abs));

  if (Math.abs(left) === minOverlap) return { x: left, y: 0 };
  if (Math.abs(right) === minOverlap) return { x: right, y: 0 };
  if (Math.abs(top) === minOverlap) return { x: 0, y: top };
  return { x: 0, y: bottom };
}

/**
 * Returns Minimum Translation Vector to push rect out of polygon (defined by points).
 * Uses Separating Axis Theorem.
 * Assumes convex polygon!
 */
export function getPolygonMtv(rect: Rect, points: Point[]): Point | null {
  const rectPoints = [
    { x: rect.x, y: rect.y },
    { x: rect.x + rect.width, y: rect.y },
    { x: rect.x + rect.width, y: rect.y + rect.height },
    { x: rect.x, y: rect.y + rect.height },
  ];

  if (!points || points.length < 3) {
    return null;
  }

  const polygons = [rectPoints, points];
  let minOverlap = Infinity;
  let smallestAxis: Point | null = null;

  for (const polygon of polygons) {
    for (let i = 0; i < polygon.length; i++) {
      const p1 = polygon[i];
      const p2 = polygon[(i + 1) % polygon.length];

      const normal = { x: p2.y - p1.y, y: p1.x - p2.x };
      // Normalize
      const len = Math.sqrt(normal.x * normal.x + normal.y * normal.y);
      if (len === 0) {
        // Degenerate edge, skip
        continue;
      }
      normal.x /= len;
      normal.y /= len;

      let minA = Infinity,
        maxA = -Infinity;
      for (const p of rectPoints) {
        const projected = normal.x * p.x + normal.y * p.y;
        if (projected < minA) minA = projected;
        if (projected > maxA) maxA = projected;
      }

      let minB = Infinity,
        maxB = -Infinity;
      for (const p of points) {
        const projected = normal.x * p.x + normal.y * p.y;
        if (projected < minB) minB = projected;
        if (projected > maxB) maxB = projected;
      }

      if (maxA < minB || maxB < minA) {
        return null;
      }

      const overlap = Math.min(maxA, maxB) - Math.max(minA, minB);
      if (overlap < minOverlap) {
        minOverlap = overlap;
        smallestAxis = normal;
      }
    }
  }

  if (smallestAxis) {
    // Ensure MTV points from polygon to rect (push rect out)
    // We need to check the direction.
    // Vector from polygon center to rect center
    const rectCenter = { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 };
    // Approximate polygon center
    let polyCenterX = 0,
      polyCenterY = 0;
    for (const p of points) {
      polyCenterX += p.x;
      polyCenterY += p.y;
    }
    polyCenterX /= points.length;
    polyCenterY /= points.length;

    const dir = { x: rectCenter.x - polyCenterX, y: rectCenter.y - polyCenterY };
    const dot = dir.x * smallestAxis.x + dir.y * smallestAxis.y;

    if (dot < 0) {
      smallestAxis.x = -smallestAxis.x;
      smallestAxis.y = -smallestAxis.y;
    }

    return { x: smallestAxis.x * minOverlap, y: smallestAxis.y * minOverlap };
  }

  return null;
}
